import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import ReactMarkdown from"react-markdown";
import remarkGfm from"remark-gfm";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";

// Optional FE map for nicer display (or import from shared utils if exposed)
const LANGUAGE_MAP = {
 en:"English",
 hi:"Hindi",
 ta:"Tamil",
 te:"Telugu",
 kn:"Kannada",
 ml:"Malayalam",
 mr:"Marathi",
 gu:"Gujarati",
 bn:"Bengali",
 pa:"Punjabi",
 ur:"Urdu",
};

const ResumeReviewPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 if (isTenantMode) {
   return <ResumeReviewPageContent mode={mode} />;
 }

 return (
   <FeatureGuard feature="TSG_AI">
     <ResumeReviewPageContent mode={mode} />
   </FeatureGuard>
 );
};

const ResumeReviewPageContent = ({ mode = "learner" }) => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [file, setFile] = useState(null);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 const isTenantMode = mode === "tenant";

 // 🔹 language from navigation state OR saved session
 const location = useLocation();
 const { session } = useSkillGenieSession();
 const langCode = (location.state?.language || session?.language ||"en").toLowerCase();
 const langName = LANGUAGE_MAP[langCode] ||"English";

 const handleUpload = (e) => setFile(e.target.files[0] || null);

 const handleAsk = async () => {
 if (!input.trim() && !file) return;

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 const userId = user?.userId ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate("/login");
 return;
 }

    const currentInput = input;
    const currentFile = file;
    setChat((prev) => [...prev, { role: "user", content: currentInput || (currentFile ? "📄 Uploaded resume" : "") }]);
    setInput("");
    setFile(null);
    setLoading(true);

    const formData = new FormData();
    formData.append("prompt", currentInput);
    formData.append("userId", userId);
    formData.append("language", langCode);
    formData.append("chatHistory", JSON.stringify(chat));
    if (currentFile) formData.append("resume", currentFile);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/resume_review`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-[#F8FAFC] text-[#111827] font-sans">
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-120px)]">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 shadow-sm">
              <svg className="w-8 h-8 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-black text-[#111827] uppercase tracking-tight">
                @tsg <span className="text-[#5B21B6]">→</span> Resume Review
              </h2>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">AI-powered CV optimization engine</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-purple-100 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">lang: {langName}</span>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar space-y-6">
          {chat.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-40">
              <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100">
                <svg className="w-10 h-10 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-sm text-[#111827]">Ready to review your resume?</p>
                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-tighter">Upload your resume or paste content for AI-powered feedback.</p>
              </div>
            </div>
          ) : (
            chat.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] p-5 rounded-[24px] shadow-sm border transition-all ${
                    msg.role === "user"
                      ? "bg-white text-[#111827] rounded-tr-none border-purple-100 shadow-purple-50"
                      : "bg-white text-[#111827] rounded-tl-none border-gray-100 shadow-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-wider ${msg.role === "user" ? "text-[#5B21B6]" : "text-gray-400"}`}>
                      {msg.role === "user" ? "You" : "TSG Expert"}
                    </span>
                  </div>
                  <div className={`prose max-w-none prose-p:leading-relaxed prose-headings:text-[#5B21B6] prose-strong:text-[#5B21B6] ${msg.role === 'user' ? 'prose-p:text-[#111827]' : 'prose-p:text-gray-600'}`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p {...props} className="mb-0" />,
                        table({ children }) {
                          return (
                            <div className="overflow-x-auto my-4 rounded-lg border border-purple-100/20">
                              <table className="min-w-full divide-y divide-purple-100">
                                {children}
                              </table>
                            </div>
                          );
                        },
                        th({ children }) {
                          return (
                            <th className="px-4 py-2 bg-purple-50 text-left text-[10px] font-black text-[#5B21B6] uppercase tracking-wider">
                              {children}
                            </th>
                          );
                        },
                        td({ children }) {
                          return (
                            <td className="px-4 py-2 text-sm border-t border-purple-50 text-gray-600">
                              {children}
                            </td>
                          );
                        },
                        code: ({ node, inline, ...props }) => (
                          <code
                            {...props}
                            className={`${
                              inline
                                ? "bg-purple-50 text-[#5B21B6] px-1.5 py-0.5 rounded text-sm font-bold"
                                : "block bg-gray-900 text-green-400 p-4 rounded-xl border border-gray-800 font-mono text-sm my-4 overflow-x-auto"
                            }`}
                          />
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Sticky Input Area */}
        <div className="relative">
          {loading && (
            <div className="absolute -top-10 left-4 flex items-center gap-2 text-[#5B21B6] text-[10px] font-black uppercase tracking-widest">
              <span className="flex space-x-1">
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce"></span>
              </span>
              TSG is analyzing...
            </div>
          )}
          <div className="flex flex-col gap-3 p-4 bg-white rounded-[24px] border border-purple-100 shadow-xl shadow-purple-50 focus-within:border-[#5B21B6] focus-within:ring-4 focus-within:ring-purple-50 transition-all">
            <div className="flex items-center gap-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAsk()}
                placeholder="Ask about your resume or improvements..."
                className="flex-1 bg-transparent px-2 py-1 text-gray-900 placeholder-gray-400 focus:outline-none"
              />
              <button
                onClick={handleAsk}
                disabled={loading || (!input.trim() && !file)}
                className="p-3 rounded-xl bg-primary-purple text-white hover:bg-opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed group shadow-md"
              >
                <svg className={`w-6 h-6 transform ${loading ? 'animate-spin' : 'group-hover:translate-x-1 group-hover:-translate-y-1'} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="p-2 rounded-lg bg-gray-50 border border-gray-200 group-hover:border-primary-purple/50 transition-colors">
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
                  {file ? file.name : "Attach Resume (PDF/DOC)"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
              {file && (
                <button 
                  onClick={() => setFile(null)}
                  className="text-xs text-red-500 hover:text-red-400 font-medium"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeReviewPage;



